import {Component, computed, ElementRef, inject, Input, QueryList, ViewChildren,} from '@angular/core';
import {FormControl, ReactiveFormsModule} from '@angular/forms';

import {CategoryConfig, FieldType} from '../../models/form-config.model';
import {DynamicFormService} from '../../services/dynamic-form.service';
import {FieldRendererComponent} from '../field-renderer/field-renderer.component';
import {ValidationMessagePipe} from '../../pipes/validation-message.pipe';

/**
 * CategoryAccordionComponent — singola sezione accordion del form.
 *
 * Responsabilità:
 * - Mostra titolo + stato validità nella testata
 * - Gestisce apertura/chiusura tramite DynamicFormService. Di base tutte le
 *   categorie sono aperte; ciascuna si apre/chiude in modo indipendente.
 * - Renderizza i campi visibili tramite FieldRendererComponent
 * - Espone focusFirstField() chiamato da DynamicFormComponent quando la
 *   categoria viene aperta
 */
@Component({
  selector: 'app-category-accordion',
  standalone: true,
  imports: [ReactiveFormsModule, FieldRendererComponent, ValidationMessagePipe],
  templateUrl: './category-accordion.component.html',
  styleUrl: './category-accordion.component.scss',
})
export class CategoryAccordionComponent {

  @Input({required: true}) category!: CategoryConfig;

  /** Riferimenti ai wrapper di ogni campo, usati per il focus. */
  @ViewChildren('fieldWrapper') fieldWrappers!: QueryList<ElementRef<HTMLElement>>;

  protected readonly svc = inject(DynamicFormService);

  // ── Computed ─────────────────────────────────────────────────────────────

  /** Esposto per DynamicFormComponent.accordionRefs.find() */
  get categoryKey(): string {
    return this.category.key;
  }

  /** Estrae gli errori a livello di FormGroup (cross-field validators) */
  protected categoryErrors = computed(() => {
    this.svc.snapshot(); // Forza la dipendenza dal segnale per aggiornamento in tempo reale
    return this.categoryGroup()?.errors;
  });

  /**
   * Determina se mostrare l'errore di categoria.
   * Lo mostriamo se ci sono errori e se il gruppo è 'touched'
   * (ovvero l'utente ha provato a proseguire o ha interagito con i campi).
   */
  protected hasError = computed(() => this.svc.hasCategoryError(this.category.key));

  protected isOpen = computed(
    () => this.svc.isCategoryOpen(this.category.key),
  );

  protected isValid = computed(
    () => this.svc.isCategoryValid(this.category.key),
  );

  protected isPending = computed(
    () => this.svc.isCategoryPending(this.category.key),
  );

  /** FormGroup della categoria per il binding [formGroupName]. */
  protected categoryGroup = computed(
    () => this.svc.getCategoryGroup(this.category.key),
  );

  /** Campi della categoria filtrati per visibilità, associati al loro FormControl. */
  protected fieldsWithControls = computed(() => {
    const snapshot = this.svc.snapshot();
    const group = this.categoryGroup();
    return this.category.fields
    .filter(f => !f.showIf || f.showIf(snapshot))
    .map(field => ({
      field,
      control: group.get(field.key) as FormControl
    }));
  });

  /**
   * True se la categoria ha un solo campo. In tal caso la griglia passa a
   * una singola colonna, così il campo occupa tutta la riga.
   */
  protected isSingleField = computed(() => this.fieldsWithControls().length === 1);

  /**
   * True se la categoria ha un solo campo E quel campo è di tipo "compatto"
   * (non un elenco/gruppo che deve occupare tutta la larghezza della modale,
   * come un gruppo di checkbox, un multiselect o una textarea).
   * Usato per restringere a `fit-content` la larghezza della sola categoria
   * con un singolo campo stretto (es. un solo dropdown).
   */
  protected isCompactSingleField = computed(() => {
    const fields = this.fieldsWithControls();
    if (fields.length !== 1) return false;

    const {type, options, radioCards} = fields[0].field;
    const isWideList = (type === 'checkbox' && !!options?.length)
      || type === 'multiselect'
      || type === 'textarea'
      // I riquadri si dividono la larghezza della sezione: a fit-content non
      // avrebbero spazio da dividersi e finirebbero uno sotto l'altro.
      || (type === 'radio' && !!radioCards)
      // Le card occupano la riga per definizione: strizzarle a fit-content
      // quando restano sole nella sezione (es. il PIN già registrato, con i
      // suoi campi ancora chiusi) le ridurrebbe a un rettangolo storto.
      || this.isFullWidth(fields[0].field);
    return !isWideList;
  });

  /**
   * True per i campi che occupano tutte le colonne della griglia invece di
   * condividere la riga con il campo successivo: le card, che portano titolo,
   * descrizione e un comando sulla stessa linea.
   */
  protected isFullWidth(field: {type: FieldType}): boolean {
    return field.type === 'module-toggle' || field.type === 'secret-state';
  }

  // ── Azioni ───────────────────────────────────────────────────────────────

  /** Alterna liberamente lo stato aperto/chiuso di questa categoria. */
  toggle(): void {
    this.svc.toggleCategory(this.category.key);
  }

  /**
   * Sposta il focus sul primo input della categoria.
   * Chiamato da DynamicFormComponent quando la categoria viene aperta.
   */
  focusFirstField(): void {
    const first = this.fieldWrappers?.first;
    if (!first) return;
    const focusable = first.nativeElement.querySelector<HTMLElement>(
      'input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
    );
    focusable?.focus();
  }
}
